import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppIcon from "../../../components/ui/AppIcon";
import AppLogo from "../../../components/ui/AppLogo";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import CreateDirectChatModal from "./DirectChatUserPicker";

// ── Ink dark-inverted sidebar. Light text on near-black, hairline
//    dividers, mono-uppercase section labels. Volt is reserved for the
//    ONE live signal: the online presence dot.

export default function LeftSidebar({ onSearchOpen, onCreateChannel }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    channels,
    directMessages,
    activeChat,
    dmUnreadCounts,
    channelUnreadCounts,
    draftsStatus,
    userDrafts,
    setShowDraftsModal,
    setShowThreadsModal,
    onlineUsers,
    openUserProfile,
  } = useChat();
  const [orgName, setOrgName] = useState("Workspace");
  const [showDMModal, setShowDMModal] = useState(false);

  const role = user?.user_role?.role || user?.role || "";
  const isPrivileged = role === "admin" || role === "superadmin";

  useEffect(() => {
    const storedOrg = localStorage.getItem("org_name");
    if (storedOrg) setOrgName(storedOrg);
  }, []);

  const activeChatId = (
    activeChat?.data?._id ||
    activeChat?.data?.id ||
    ""
  ).toString();

  const PINNED_CHANNELS = ["general", "sapient-space"];
  const pinnedRank = (name) => {
    const i = PINNED_CHANNELS.indexOf((name || "").trim().toLowerCase());
    return i === -1 ? PINNED_CHANNELS.length : i;
  };
  const sortedChannels = [...channels].sort(
    (a, b) => pinnedRank(a.name) - pinnedRank(b.name),
  );

  return (
    <aside className="h-full flex overflow-hidden">
      <div className="w-full md:w-[260px] flex flex-col bg-[#161614] text-white/80 border-r border-white/10 overflow-hidden">
        {/* Brand */}
        <div className="px-4 pt-4 pb-3.5 flex items-center border-b border-white/[0.07]">
          <AppLogo size={30} showWordmark color="text-white" />
        </div>

        {/* Search */}
        <div className="px-3 mt-3 mb-3">
          <div
            onClick={onSearchOpen}
            className="flex items-center gap-2 px-3 py-2 bg-white/[0.06] border border-white/10 hover:border-white/20 text-white/45 cursor-pointer transition-colors"
          >
            <AppIcon name="MagnifyingGlassIcon" size={14} />
            <span className="text-[13px] font-medium">Search conversations</span>
            <span className="ml-auto eyebrow eyebrow-10 !text-white/30 border border-white/15 px-1">
              ⌘K
            </span>
          </div>
        </div>

        <nav className="flex-1 flex flex-col min-h-0 px-2 pt-1 overflow-hidden">
          {/* Top Level Nav */}
          <div className="space-y-0.5 flex-shrink-0 mb-4">
            <NavItem
              icon="PencilSquareIcon"
              label="Drafts"
              badge={userDrafts.length}
              onClick={() => setShowDraftsModal(true)}
            />
            <NavItem
              icon="ChatBubbleBottomCenterIcon"
              label="Threads"
              badge={0}
              onClick={() => setShowThreadsModal(true)}
            />
          </div>

          {/* Channels Group */}
          <div className="flex flex-col min-h-0 mb-4" style={{ maxHeight: "40%" }}>
            <div className="px-3 py-1 flex items-center justify-between group cursor-pointer mb-1 flex-shrink-0">
              <span className="eyebrow eyebrow-10 !text-white/40">Channels</span>
              {isPrivileged && (
                <button
                  onClick={() => {
                    if (!isPrivileged) return;
                    onCreateChannel?.();
                  }}
                  className="p-1 hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <AppIcon name="PlusIcon" size={14} className="text-white/60" />
                </button>
              )}
            </div>
            <div className="space-y-0.5 overflow-y-auto custom-scrollbar">
              {sortedChannels.map((ch) => (
                <ChannelItem
                  key={ch._id || ch.id}
                  id={ch._id || ch.id}
                  label={ch.name}
                  type={ch.isPrivate ? "private" : "public"}
                  unread={channelUnreadCounts[ch._id || ch.id] || 0}
                  hasDraft={!!draftsStatus[ch._id || ch.id]}
                  active={
                    activeChat?.type === "channel" &&
                    !!activeChatId &&
                    activeChatId === (ch._id || ch.id)?.toString()
                  }
                  onClick={() => navigate(`/channel/${ch._id || ch.id}`)}
                />
              ))}
            </div>
          </div>

          {/* DMs Group */}
          <div className="flex flex-col min-h-0 flex-1">
            <div className="px-3 py-1 flex items-center justify-between mb-1 flex-shrink-0">
              <span className="eyebrow eyebrow-10 !text-white/40">Direct Messages</span>
              <button
                onClick={() => setShowDMModal(true)}
                className="p-1 hover:bg-white/10 transition-colors"
              >
                <AppIcon name="PlusIcon" size={14} className="text-white/60" />
              </button>
            </div>
            <div className="space-y-0.5 overflow-y-auto custom-scrollbar flex-1">
              {user && (user._id || user.id) && (
                <DMItem
                  key="self-notes"
                  id={user._id || user.id}
                  label="Notes"
                  isSelf
                  avatar={
                    user.profilePhoto ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "Me")}&background=1A1A19&color=fff`
                  }
                  status="online"
                  unread={0}
                  hasDraft={false}
                  active={
                    activeChat?.type === "dm" &&
                    !!activeChatId &&
                    activeChatId === (user._id || user.id)?.toString()
                  }
                  onClick={() => navigate(`/dm/${user._id || user.id}`)}
                />
              )}
              {directMessages
                .filter((dm) => (dm._id || dm.id) !== user?.id)
                .map((dm) => (
                  <DMItem
                    key={dm._id || dm.id}
                    id={dm._id || dm.id}
                    label={dm.name || dm.displayName}
                    avatar={
                      dm.profilePhoto ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(dm.name || dm.displayName || "User")}&background=random`
                    }
                    status={
                      onlineUsers.includes(dm._id || dm.id) ? "online" : "offline"
                    }
                    unread={dmUnreadCounts[dm._id || dm.id] || 0}
                    hasDraft={!!draftsStatus[dm._id || dm.id]}
                    active={
                      activeChat?.type === "dm" &&
                      !!activeChatId &&
                      activeChatId === (dm._id || dm.id)?.toString()
                    }
                    onClick={() => navigate(`/dm/${dm._id || dm.id}`)}
                  />
                ))}
            </div>
          </div>
        </nav>

        {/* User Profile Footer */}
        <footer className="p-3 bg-black/30 border-t border-white/10 mt-auto">
          <div
            onClick={() => openUserProfile(user?._id || user?.id)}
            className="flex items-center gap-3 p-2 hover:bg-white/[0.06] cursor-pointer transition-colors group"
          >
            <div className="relative">
              <img
                src={
                  user?.profilePhoto ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "Me")}&background=1A1A19&color=fff`
                }
                className="w-9 h-9 rounded-full object-cover border border-white/15"
                alt="Me"
              />
              <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-[#161614] rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-volt rounded-full" />
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-[13px] font-semibold text-white truncate">
                {user?.name || "Me"}
              </p>
              <p className="eyebrow eyebrow-10 !text-volt">Active</p>
            </div>
            <AppIcon
              name="Cog6ToothIcon"
              size={18}
              className="text-white/40 group-hover:text-white/80 group-hover:rotate-45 transition-all"
            />
          </div>
        </footer>
      </div>

      {showDMModal && <CreateDirectChatModal onClose={() => setShowDMModal(false)} />}
    </aside>
  );
}

/* HELPER COMPONENTS — dark theme */
function NavItem({ icon, label, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-[13.5px] font-medium text-white/55 hover:bg-white/[0.06] hover:text-white transition-colors group"
    >
      <AppIcon
        name={icon}
        size={18}
        className="opacity-60 group-hover:opacity-100 transition-opacity"
      />
      <span>{label}</span>
      {badge > 0 && (
        <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 bg-white text-ink">
          {badge}
        </span>
      )}
    </button>
  );
}

function ChannelItem({ id, label, type, unread, active, hasDraft, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 cursor-pointer border-l-2 ${
        active
          ? "bg-white/[0.06] text-white font-semibold border-volt"
          : "text-white/55 hover:bg-white/[0.06] hover:text-white border-transparent"
      } ${hasDraft && !active ? "border-warning" : ""}`}
    >
      <span className="opacity-40 font-semibold w-4 text-center">
        {type === "private" ? "🔒" : "#"}
      </span>
      <span className={`flex-1 truncate ${unread > 0 ? "text-white font-semibold" : ""}`}>
        {label}
      </span>
      {unread > 0 && (
        <span className="min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-semibold px-1.5 bg-white text-ink">
          {unread}
        </span>
      )}
    </div>
  );
}

function DMItem({ id, label, avatar, status, unread, hasDraft, active, onClick, isSelf }) {
  const statusColors = {
    online: "bg-volt",
    away: "bg-away",
    busy: "bg-error",
    offline: "bg-white/30",
  };

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 cursor-pointer border-l-2 ${
        active
          ? "bg-white/[0.06] text-white font-semibold border-volt"
          : "text-white/55 hover:bg-white/[0.06] hover:text-white border-transparent"
      } ${hasDraft && !active ? "border-warning" : ""}`}
    >
      {isSelf ? (
        <div className="relative flex-shrink-0 w-6 h-6 bg-white/10 border border-white/15 flex items-center justify-center">
          <AppIcon name="Bookmark" size={13} className="text-white/70" />
        </div>
      ) : (
        <div className="relative flex-shrink-0">
          <img
            src={avatar}
            className="w-6 h-6 rounded-full object-cover border border-white/15"
            alt={label}
          />
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-[#161614] rounded-full flex items-center justify-center">
            <div className={`w-1.5 h-1.5 rounded-full ${statusColors[status] || "bg-white/30"}`} />
          </div>
        </div>
      )}
      <span className={`flex-1 truncate ${unread > 0 ? "text-white font-semibold" : ""}`}>
        {label}
      </span>
      {isSelf && (
        <span className="text-[9px] font-semibold uppercase tracking-wider text-white/40 bg-white/[0.08] px-1.5 py-0.5">
          You
        </span>
      )}
      {unread > 0 && (
        <span className="min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-semibold px-1.5 bg-white text-ink">
          {unread}
        </span>
      )}
    </div>
  );
}
